// The words on the combined "Routine cleanup" card: how many routine threads, and one line on what kinds.
// The operator writes each group's summary as "Archive routine mail: A, B, C."; across several
// groups of one round the kinds repeat, so they are merged into one short line.
/** The parts of a group the words need (a full RoutineGroupView fits). */
export interface CleanupGroupWords {
  label: string;
  summary: string;
  items: readonly unknown[];
}
type GroupWords = CleanupGroupWords;

const KINDS_PREFIX = /^archive routine mail:\s*/i;
/** "Routine cleanup (1 of 4)" -> "Routine cleanup". */
const PART_SUFFIX = /\s*\(\d+\s+of\s+\d+\)\s*$/i;

/** Threads across every group. */
export function cleanupCount(groups: readonly { items: readonly unknown[] }[]): number {
  return groups.reduce((sum, g) => sum + g.items.length, 0);
}

/** "N routine threads" ("1 routine thread"). */
export function cleanupTitle(count: number): string {
  return `${count} routine ${count === 1 ? "thread" : "threads"}`;
}

/** A group label without its "(1 of 4)" part marker. */
export function cleanupLabel(label: string): string {
  return label.replace(PART_SUFFIX, "").trim();
}

/** Distinct labels in first-seen order (case-insensitive), part markers removed. */
export function cleanupLabels(groups: readonly { label: string }[]): string[] {
  return dedupe(groups.map((g) => cleanupLabel(g.label)));
}

function dedupe(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const key = value.toLowerCase();
    if (!value || seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}

/** The kinds a "Archive routine mail: A, B." summary names (empty for any other wording). */
function kindsOf(summary: string): string[] {
  const text = summary.trim();
  if (!KINDS_PREFIX.test(text)) return [];
  return text
    .replace(KINDS_PREFIX, "")
    .replace(/\.+\s*$/, "")
    .split(",")
    .map((k) => k.trim().replace(/\.+$/, "").trim())
    .filter(Boolean);
}

/** "Newsletters, receipts, promotions." The first kind keeps its capital; the rest are lower. */
function sentence(kinds: readonly string[]): string {
  const words = kinds.map((k, i) =>
    i === 0 ? k.charAt(0).toUpperCase() + k.slice(1) : k.toLowerCase(),
  );
  return `${words.join(", ")}.`;
}

/** One muted line on what is in the cleanup. Empty when there are no groups. */
export function cleanupSummary(groups: readonly GroupWords[]): string {
  if (groups.length === 0) return "";
  const kinds = dedupe(groups.flatMap((g) => kindsOf(g.summary)));
  if (kinds.length > 0) return sentence(kinds);
  const labels = cleanupLabels(groups);
  if (labels.length > 1) return sentence(labels);
  const summary = groups.find((g) => g.summary.trim())?.summary.trim();
  return summary ?? (labels.length === 1 ? sentence(labels) : "");
}
